import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const AUTH_API_BASE = `${API_ROOT}/api/auth`;
const BASIC_ALLOWED_CURRENT_MODULES = [2, 3, 4, 5, 6, 8, 9, 10, 11, 12, 15, 20, 22, 23, 24, 25, 26, 27, 28];

export default function BasicRole() {
  const navigate = useNavigate();
  const storedUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const [mobile] = useState(storedUser?.mobile || "");
  const [moduleId, setModuleId] = useState("");
  const [modules, setModules] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const currentModuleId = parseInt(storedUser?.moduleId, 10);

  useEffect(() => {
    const fetchBasicModules = async () => {
      if (!mobile) {
        setError("Missing mobile in session. Please login again.");
        setLoading(false);
        return;
      }

      if (!BASIC_ALLOWED_CURRENT_MODULES.includes(currentModuleId)) {
        setError("Unauthorized access Or Session Timed out.");
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${AUTH_API_BASE}/basic-modules?login=${encodeURIComponent(mobile)}`);
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to fetch basic roles");
        }

        setModules(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || "Failed to fetch basic roles");
      } finally {
        setLoading(false);
      }
    };

    fetchBasicModules();
  }, [mobile, currentModuleId]);

  const handleSwitch = async (e) => {
    e.preventDefault();
    setError("");

    if (!moduleId) {
      setError("Please select module.");
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${AUTH_API_BASE}/switch-basic-role`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          login: mobile,
          moduleId,
          currentModuleId: storedUser?.moduleId
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to switch role");
      }

      const moduleName = modules.find((m) => String(m.ModuleID) === String(moduleId))?.Module || "";
      localStorage.setItem("user", JSON.stringify({ ...data, moduleName }));
      const resolvedModuleId = parseInt(data?.moduleId, 10);
      const target = resolvedModuleId === 1
        ? "/profile"
        : resolvedModuleId === 10
          ? "/guesthouse/pending-bookings"
          : "/dashboard";
      navigate(target);
    } catch (err) {
      setError(err.message || "Failed to switch role");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: "560px" }}>
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h3 className="mb-0">Back to Employee Role</h3>
        <Link to="/switch-role" className="btn btn-outline-secondary btn-sm">
          Assigned Roles
        </Link>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSwitch}>
        <div className="mb-3">
          <label className="form-label">User Login (Mobile)</label>
          <input className="form-control" value={mobile} readOnly />
        </div>

        <div className="mb-3">
          <label className="form-label">Select Module</label>
          <select
            className="form-control"
            value={moduleId}
            onChange={(e) => setModuleId(e.target.value)}
            required
            disabled={loading || !modules.length}
          >
            <option value="">Select Module</option>
            {modules.map((m) => (
              <option key={`${m.ModuleID}-${m.UserID || "basic"}`} value={m.ModuleID}>
                {m.Module}
              </option>
            ))}
          </select>
        </div>

        <button className="btn btn-primary" type="submit" disabled={loading || submitting || !modules.length}>
          {submitting ? "Switching..." : "Switch"}
        </button>
      </form>
    </div>
  );
}
